import {
  getBookingPackageLines,
  parseBookingRequirements,
  resolvePackageLineName
} from '../components/VenueDashboard/utils/bookingAddOns';
import { parseMoney } from './bookingAmounts';

const formatMoney = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(value);
};

const displayOrDash = (value) => {
  if (value == null) return '—';
  const trimmed = String(value).trim();
  return trimmed || '—';
};

export const isInHouseCateringPreference = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('in-house') || normalized === 'in-house';
};

export const formatBookingCatering = (booking) => {
  const req = parseBookingRequirements(booking);
  return displayOrDash(
    req.catering_preference ??
      req.catering ??
      booking?.catering_preference ??
      booking?.catering
  );
};

export const formatBookingDietaryRestrictions = (booking) => {
  const req = parseBookingRequirements(booking);
  const catering =
    req.catering_preference ??
    req.catering ??
    booking?.catering_preference ??
    booking?.catering;
  if (!isInHouseCateringPreference(catering)) return '—';
  const hasRestrictions = Boolean(
    req.has_dietary_restrictions ??
      booking?.has_dietary_restrictions
  );
  if (!hasRestrictions) return 'None specified';
  return displayOrDash(
    req.dietary_restrictions ??
      booking?.dietary_restrictions
  );
};

export const formatBookingAlcohol = (booking) => {
  const req = parseBookingRequirements(booking);
  return displayOrDash(
    req.alcohol_consumption ??
      req.alcohol ??
      booking?.alcohol_consumption ??
      booking?.alcohol
  );
};

const truthyRequirementFlag = (...values) => {
  for (const value of values) {
    if (value == null || value === '') continue;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['yes', 'true', '1', 'needed'].includes(normalized)) return true;
      if (['no', 'false', '0'].includes(normalized)) return false;
      continue;
    }
    return Boolean(value);
  }
  return false;
};

export const bookingNeedsAv = (booking) => {
  const req = parseBookingRequirements(booking);
  return truthyRequirementFlag(
    req.needs_av,
    req.audio_visual,
    req.av_needed,
    booking?.needs_av,
    booking?.audio_visual
  );
};

export const formatBookingAv = (booking) => (bookingNeedsAv(booking) ? 'Yes' : 'No');

export const formatBookingAddOnsSummary = (booking, options = {}) => {
  const catalogById = options.catalogById;
  const raw = getBookingPackageLines(booking);
  if (!Array.isArray(raw) || raw.length === 0) return '—';

  const lines = raw
    .map((line) => {
      if (!line || typeof line !== 'object') return null;
      const qtyRaw = line.quantity ?? line.qty;
      const qty = qtyRaw == null || qtyRaw === '' ? 1 : Math.max(0, Number(qtyRaw) || 0);
      if (qty <= 0) return null;
      const name = resolvePackageLineName(line, catalogById);
      const total = parseMoney(line.total ?? line.amount);
      if (total != null && total > 0) {
        return `${name} x${qty} (${formatMoney(total)})`;
      }
      return `${name} x${qty}`;
    })
    .filter(Boolean);

  return lines.length > 0 ? lines.join('; ') : '—';
};

const formatNoteTimestamp = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Inline enquiry / special requirements on the booking row. */
export const formatBookingNotesSummary = (booking) => {
  const req = parseBookingRequirements(booking);
  const parts = [
    req.special_requirements,
    req.message,
    req.booking_notes,
    req.notes,
    booking?.special_requirements,
    booking?.notes,
    booking?.booking_notes
  ]
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean);
  if (parts.length === 0) return '—';
  return [...new Set(parts)].join(' · ');
};

/** Venue thread notes from GET /bookings/{id}/notes (Manage → Notes tab). */
export const formatBookingThreadNotes = (notes) => {
  if (!Array.isArray(notes) || notes.length === 0) return '';
  return notes
    .map((note) => {
      const body = (note?.body ?? note?.text ?? note?.content ?? '').trim();
      if (!body) return null;
      const author =
        note?.created_by_name ??
        note?.author_name ??
        note?.created_by_email ??
        note?.source ??
        'Note';
      const when = formatNoteTimestamp(note?.created_at ?? note?.updated_at);
      return when ? `${author} (${when}): ${body}` : `${author}: ${body}`;
    })
    .filter(Boolean)
    .join('\n');
};

/** UUID, booking_id, and booking_reference — list rows may use different keys. */
export const bookingNoteLookupKeys = (booking) => {
  const keys = new Set();
  [booking?.id, booking?.booking_id, booking?.booking_reference].forEach((value) => {
    const key = value == null ? '' : String(value).trim();
    if (key) keys.add(key);
  });
  return [...keys];
};

export const resolveNotesForBookingExport = (booking, notesByBookingId = null) => {
  if (!notesByBookingId) return null;
  for (const key of bookingNoteLookupKeys(booking)) {
    const notes = notesByBookingId[key];
    if (Array.isArray(notes) && notes.length > 0) return notes;
  }
  return null;
};

export const formatBookingNotesForPdf = (booking, notesByBookingId = null) => {
  const threadNotes = formatBookingThreadNotes(
    resolveNotesForBookingExport(booking, notesByBookingId)
  );
  const inline = formatBookingNotesSummary(booking);
  const inlineText = inline === '—' ? '' : inline;
  if (threadNotes && inlineText) {
    return `${inlineText}\n\n${threadNotes}`;
  }
  return threadNotes || inlineText || '—';
};
